/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn } from '@fluentui/react/lib/DetailsList';
import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { NameCellLink } from 'src/core/components/NameCellLink';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { Feature } from 'src/luis/modules/features/types/Feature';
import { Intent } from 'src/luis/modules/intents/Intent';
import { LuisId } from 'src/luis/types/Resource';
import { ConversationFeaturePickerContainer } from 'src/luis/verticals/conversationFeaturePicker/ConversationFeaturePickerContainer';

export type IntentTableProps = TableProps<Intent> & { features: Feature[]; onIntentClick: (intentId: LuisId) => void };

const messages = defineMessages({
    nameColumnHeader: {
        id: 'intents.intentTable.nameColumnHeader',
        defaultMessage: 'Name'
    },
    examplesCountColumnHeader: {
        id: 'intents.intentTable.examplesCountColumnHeader',
        defaultMessage: 'Examples'
    },
    featuresCountColumnHeader: {
        id: 'intents.intentTable.featuresCountColumnHeader',
        defaultMessage: 'Features'
    },
    emptyTablePlaceholder: {
        id: 'intents.intentTable.emptyTablePlaceholder',
        defaultMessage: "You haven't created any intents yet"
    }
});

/**
 * Displays a table of intents and their features and examples counts.
 *
 * @param props The input properties for the table.
 */
export const IntentTable = (props: IntentTableProps) => {
    const { items, selection, features, sortKey, onIntentClick, ...rest } = props;
    const { localize } = useLocalization();

    /**
     * A custom renderer function for the features name.
     *
     * @param item The intent being rendered.
     */
    const renderFeatures = (item: Intent) => (
        <ConversationFeaturePickerContainer telemetryContext="IntentTable" model={item} key={item.id} />
    );

    /**
     * Intent table columns.
     */
    const columns: IColumn[] = [
        {
            key: 'name',
            minWidth: 300,
            maxWidth: 500,
            fieldName: 'name',
            isRowHeader: true,
            isResizable: true,
            name: localize(messages.nameColumnHeader),
            isSorted: sortKey && !!(sortKey.key === 'name'),
            isSortedDescending: sortKey && !!(sortKey.key === 'name') && sortKey?.order === 'desc',
            onRender: (item: Intent) => <NameCellLink onClick={() => onIntentClick(item.id)}>{item.name}</NameCellLink>
        },
        {
            minWidth: 100,
            maxWidth: 100,
            isResizable: true,
            key: 'labelsCount',
            isCollapsible: true,
            fieldName: 'examples',
            name: localize(messages.examplesCountColumnHeader),
            isSorted: sortKey && !!(sortKey.key === 'labelsCount'),
            isSortedDescending: sortKey && !!(sortKey.key === 'labelsCount') && sortKey?.order === 'desc',
            onRender: (item: Intent) => (
                <Stack verticalFill verticalAlign="center">
                    {item.labelsCount || 0}
                </Stack>
            )
        },
        {
            minWidth: 200,
            key: 'features',
            isResizable: true,
            isCollapsible: true,
            fieldName: 'features',
            onRender: renderFeatures,
            name: localize(messages.featuresCountColumnHeader)
        }
    ];

    return (
        <Table<Intent>
            items={items}
            columns={columns}
            selection={selection}
            emptyPlaceholder={localize(messages.emptyTablePlaceholder)}
            {...rest}
        />
    );
};
